import React, { useEffect, useMemo, useRef, useState } from 'react'
import './FileViewerStyle.css'
import { Modal } from 'react-bootstrap'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'

type FileViewerProps = {
  show: boolean
  onHide: () => void
  title: string
  sourcefile?: File
  sourceUrl?: string
}

/**
 * Guess MIME type from a filename or URL if the File.type is missing.
 */
function guessMimeFromName(name?: string): string | undefined {
  if (!name) return undefined
  const lower = name.toLowerCase()
  if (lower.endsWith('.pdf')) return 'application/pdf'
  if (/\.(png)$/.test(lower)) return 'image/png'
  if (/\.(jpe?g)$/.test(lower)) return 'image/jpeg'
  if (/\.(gif)$/.test(lower)) return 'image/gif'
  return undefined
}

/**
 * Normalize a Windows-like path that points inside /public in dev,
 * to a URL the browser can fetch (like the original code intended).
 */
function normalizeDevPublicUrl(raw?: string): string | undefined {
  if (!raw) return undefined
  if (process.env.NODE_ENV !== 'development') return raw
  // Handles "C:\project\public\folder\file.ext" → "/folder/file.ext"
  const parts = raw.split(/public[\\/]/i)
  if (parts.length > 1) {
    return '/' + parts[1].replace(/\\/g, '/')
  }
  return raw
}

const FileViewer: React.FC<FileViewerProps> = ({
  show,
  onHide,
  title,
  sourcefile,
  sourceUrl,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // Build a preview URL (object URL for File; normalized path for dev public; or raw URL)
  const previewUrl = useMemo(() => {
    if (sourcefile) return objectUrl
    if (sourceUrl?.startsWith('data:')) return sourceUrl // already a data URL
    return normalizeDevPublicUrl(sourceUrl)
  }, [sourcefile, sourceUrl, objectUrl])

  // Work out the MIME type: prefer File.type, else guess from file name / url
  const mimeType = useMemo(() => {
    if (sourcefile?.type) return sourcefile.type
    return guessMimeFromName(sourcefile?.name || sourceUrl)
  }, [sourcefile, sourceUrl])

  const isPdf =
    (mimeType ?? '').includes('pdf') || /\.pdf(\?|#|$)/i.test(sourceUrl || '')

  // Create & cleanup object URL for File
  useEffect(() => {
    if (!sourcefile) {
      // clear previous objectUrl if we switch away from a File
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        setObjectUrl(null)
      }
      return
    }
    const url = URL.createObjectURL(sourcefile)
    setObjectUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcefile])

  useEffect(() => {
    if (imgRef.current && !isPdf) {
      setTimeout(() => {
        imgRef.current?.click()
      }, 500)
    }
  }, [previewUrl, isPdf])

  return previewUrl && !isPdf ? (
    <div style={{ display: 'none' }}>
      <PhotoProvider
        onVisibleChange={(visible: boolean) => {
          if (!visible) {
            onHide()
          }
        }}
        toolbarRender={({
          onScale,
          scale,
          onRotate,
          rotate,
        }: {
          onScale: (scale: number) => void
          scale: number
          onRotate: (rotate: number) => void
          rotate: number
        }) => {
          return (
            <>
              <span
                className="PhotoView-Slider__toolbarIcon icon mgc_add_circle_line"
                onClick={() => onScale(scale + 1)}
              />
              <span
                className="PhotoView-Slider__toolbarIcon icon mgc_minus_circle_line"
                onClick={() => onScale(scale - 1)}
              />
              <span
                className="PhotoView-Slider__toolbarIcon icon mgc_refresh_3_line"
                onClick={() => onRotate(rotate + 90)}
              />
            </>
          )
        }}
      >
        <PhotoView src={previewUrl}>
          <img ref={imgRef} src={previewUrl} alt="Preview" />
        </PhotoView>
      </PhotoProvider>
    </div>
  ) : (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="file-viewer-modal p-0 m-0"
      size="lg"
      fullscreen={true}
    >
      <Modal.Body className="p-0">
        <span className="pdf icon mgc_close_circle_line" onClick={onHide} />
        {/* Nothing to show yet */}
        {!previewUrl && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '100%' }}
          >
            <span className="text-muted">No file selected</span>
          </div>
        )}

        {/* PDF viewer */}
        {previewUrl && isPdf && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <object
              data={previewUrl}
              type="application/pdf"
              width="100%"
              height="100%"
              aria-label="PDF preview"
            >
              <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ height: '100%', gap: '8px' }}
              >
                <span className="text-muted">
                  PDF preview is not available in this browser.
                </span>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  Open or download PDF
                </a>
              </div>
            </object>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default FileViewer
